// src/components/HeaderWithAd/WebDesignBanner.jsx
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

import GraffitiHeroSummer from "../../assets/hero1.png";
import GraffitiHeroAutumn from "../../assets/new-hero.webp";
import Logo from "../../assets/logo-buukkeri-dark.webp";

export default function WebDesignBanner({
  children,
  bgImage,
  tall = false,
  hideLogo = false,
  hideLogoMobile = false,

  // Etusivun kompaktimpi hero
  compact = false,

  // VIDEO
  mp4Src = "",
  poster = "",
}) {
  const [theme, setTheme] = useState(() => {
    if (typeof document === "undefined") return "light";

    return document.documentElement.classList.contains("dark")
      ? "dark"
      : "light";
  });

  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (
      typeof document === "undefined" ||
      typeof MutationObserver === "undefined"
    ) {
      return;
    }

    const obs = new MutationObserver(() => {
      setTheme(
        document.documentElement.classList.contains("dark")
          ? "dark"
          : "light"
      );
    });

    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => obs.disconnect();
  }, []);

  // Jos bgImage annettu → käytä sitä.
  // Muuten poster tai teeman mukainen oletuskuva.
  const heroImage =
    bgImage ||
    poster ||
    (theme === "dark"
      ? GraffitiHeroAutumn
      : GraffitiHeroSummer);

  const canPlayVideo =
    Boolean(mp4Src) &&
    !prefersReducedMotion;

const headerSize = tall
  ? `
      h-auto
      min-h-[920px]
      xxxsm:min-h-[1450px]
      xxsm:min-h-[1200px]
      xsm:min-h-[1100px]
      sm:min-h-[1200px]
      md:min-h-[1300px]
    `
  : compact
    ? `
        min-h-[100svh]
        sm:min-h-[calc(100svh-64px)]
      `
    : `
        min-h-[560px]
        h-screen
      `;

  return (
    <header
      className={`
        relative
        w-full
        overflow-visible
        ${headerSize}
      `}
    >
      {/* =====================================================
          TAUSTA
      ====================================================== */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 will-change-transform"
          initial={{ scale: 1.02 }}
          animate={
            prefersReducedMotion
              ? { scale: 1.02 }
              : {
                  scale: [1.02, 1.065, 1.02],
                }
          }
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : {
                  duration: 16,
                  ease: "easeInOut",
                  repeat: Infinity,
                }
          }
        >
          {canPlayVideo ? (
            <video
              className="
                absolute inset-0
                h-full w-full
                object-cover
                object-[50%_30%]
              "
              src={mp4Src}
              poster={heroImage}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-hidden="true"
              tabIndex={-1}
              disablePictureInPicture
            />
          ) : (
            <img
              src={heroImage}
              alt=""
              aria-hidden="true"
              className="
                absolute inset-0
                h-full w-full
                object-cover
                object-[50%_30%]
              "
            />
          )}
        </motion.div>

        {/* Tumma kontrasti tekstin alle */}
        <div
          className="
            absolute inset-0
            bg-gradient-to-b
            from-black/85
            via-black/25
            to-black/65
          "
        />

        {/* Keskialueen hienovarainen valo */}
        <div
          className="
            absolute
            left-1/2 top-[42%]
            h-[520px] w-[900px]
            max-w-[95vw]
            -translate-x-1/2 -translate-y-1/2
            rounded-full
            bg-white/[0.04]
            blur-[90px]
          "
        />

        {/* Vignette */}
        <div
          className="
            absolute inset-0
            bg-[radial-gradient(circle_at_center,transparent_30%,rgba(0,0,0,0.46)_100%)]
          "
        />
      </div>

      {/* =====================================================
          HERO-SISÄLTÖ
      ====================================================== */}
      <div
        className={
        compact
            ? `
                relative z-10
                pointer-events-none

                flex
                min-h-[100svh]

                flex-col
                items-center
                justify-start

                px-0

                pt-[120px]
                pb-6

                sm:min-h-[720px]
                sm:justify-center
                sm:pt-[80px]
                sm:pb-4

                md:pt-[120px]

                lg:min-h-[100svh]
                lg:pt-[140px]
                lg:pb-4
              `
            : `
                absolute inset-0 z-10
                pointer-events-none

                flex flex-col
                items-center
                justify-start

                xxxsm:pt-2
                pt-20

                lg:justify-center
                lg:pt-0
              `
        }
      >
        <div className="pointer-events-auto w-full">
          <div
            className="
              mx-auto
              flex
              max-w-7xl
              flex-col
              items-center

              px-4
              text-center

              sm:px-6
              lg:px-8
            "
          >
            {/* =================================================
                BUUKKERI BRAND REVEAL
            ================================================== */}
            {!hideLogo && (
              <div
                className={
                  hideLogoMobile
                    ? "bbx-keep-shadow hidden sm:block"
                    : "bbx-keep-shadow block"
                }
              >
                <motion.div
                  initial={
                    prefersReducedMotion
                      ? false
                      : {
                          opacity: 0,
                          y: -10,
                          scale: 0.96,
                        }
                  }
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                  }}
                  transition={{
                    duration: 0.65,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className={
                    compact
                      ? `
                          relative
                          mb-3
                          sm:mb-5

                          flex
                          items-center
                          justify-center

                          gap-2
                          sm:gap-2.5
                        `
                      : `
                          relative isolate
                          mt-24
                          md:mt-32

                          flex
                          flex-col
                          items-center
                          justify-center

                          px-6
                          sm:px-10
                          md:px-12

                          py-1
                          sm:py-2
                        `
                  }
                >
                  {/* Logo */}
                  <motion.img
                    src={Logo}
                    alt=""
                    aria-hidden="true"
                    initial={
                      prefersReducedMotion
                        ? false
                        : {
                            opacity: 0,
                            rotate: -8,
                            scale: 0.8,
                          }
                    }
                    animate={{
                      opacity: 1,
                      rotate: 0,
                      scale: 1,
                    }}
                    transition={{
                      duration: 0.7,
                      delay: 0.05,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className={
                      compact
                        ? `
                            h-7 w-7
                            sm:h-10 sm:w-10

                            object-contain

                            drop-shadow-[0_4px_12px_rgba(0,0,0,0.35)]
                          `
                        : `
                            bbx-keep-shadow
                            bbx-hero-logo-shadow

                            absolute
                            left-1/2
                            top-[105%]
                            lg:top-[80%]

                            -translate-x-1/2
                            -translate-y-1/2

                            w-[clamp(140px,22vw,300px)]

                            opacity-[0.25]

                            select-none
                            pointer-events-none
                            z-0
                          `
                    }
                  />

                  {/* BUUKKERI */}
                  <motion.span
                    initial={
                      prefersReducedMotion
                        ? false
                        : {
                            opacity: 0,
                            x: compact ? -8 : 0,
                            y: compact ? 0 : 8,
                          }
                    }
                    animate={{
                      opacity: 1,
                      x: 0,
                      y: 0,
                    }}
                    transition={{
                      duration: 0.55,
                      delay: 0.15,
                      ease: "easeOut",
                    }}
                    className={
                      compact
                        ? `
                            font-customFont2

                            text-xl
                            sm:text-3xl

                            font-extrabold
                            tracking-tight

                            text-white
                            text-shadow-xl
                          `
                        : `
                            bbx-keep-shadow
                            bbx-hero-logo-shadow

                            relative z-10
                            inline-block
                            translate-x-[6px]

                            text-2xl
                            s24:text-3xl
                            xsm:text-4xl
                            sm:text-5xl
                            md:text-6xl

                            tracking-[0.28em]
                            uppercase

                            font-customFont2
                            font-extrabold

                            text-white/80
                            text-shadow-xxl
                            dark:text-shadow-xxl
                          `
                    }
                  >
                    Buukkeri
                  </motion.span>

                  {/* Reveal-viiva vain compact-herossa */}
                  {compact && (
                    <motion.div
                      initial={
                        prefersReducedMotion
                          ? false
                          : {
                              scaleX: 0,
                              opacity: 0,
                            }
                      }
                      animate={{
                        scaleX: 1,
                        opacity: 1,
                      }}
                      transition={{
                        duration: 0.55,
                        delay: 0.35,
                        ease: "easeOut",
                      }}
                      className="
                        absolute

                        -bottom-1.5
                        sm:-bottom-2

                        left-1/2

                        h-px
                        w-24

                        -translate-x-1/2

                        origin-center

                        bg-gradient-to-r
                        from-transparent
                        via-white/60
                        to-transparent
                      "
                    />
                  )}
                </motion.div>
              </div>
            )}

            {/* =================================================
                HERO CHILDREN
                H1 + haku + pillit + testimonial
            ================================================== */}
            <motion.div
              initial={
                prefersReducedMotion
                  ? false
                  : {
                      opacity: 0,
                      y: 14,
                    }
              }
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.6,
                delay: 0.18,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="
                bbx-keep-shadow
                relative z-10
                w-full
              "
            >
              {children}
            </motion.div>
          </div>
        </div>
      </div>
    </header>
  );
}